import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AppLayout } from "@/components/app-layout";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/components/language-provider";
import { getApiUrl } from "@/lib/config";
import { User, Camera, Building2, Mail, Shield, Save, Crown, Star, Zap, Lock, Eye, EyeOff } from "lucide-react";

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function Profile() {
  const [, setLocation] = useLocation();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [user, setUser] = useState<any>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (!stored) { setLocation("/login"); return; }
    const u = JSON.parse(stored);
    setUser(u);
    setFirstName(u.firstName || "");
    setLastName(u.lastName || "");

    // Refresh user data from the server so fields added after the current
    // session was created (e.g. cpfMasked/hasCpf) show up without requiring
    // the user to log out and back in.
    (async () => {
      try {
        const res = await fetch(getApiUrl("/api/auth/me"), { headers: getAuthHeaders() });
        if (!res.ok) return;
        const json = await res.json();
        const fresh = json?.data?.user ?? json?.data ?? json?.user ?? json;
        if (fresh && typeof fresh === "object" && fresh.email) {
          setUser(fresh);
          setFirstName(fresh.firstName || "");
          setLastName(fresh.lastName || "");
          localStorage.setItem("user", JSON.stringify(fresh));
        }
      } catch {
        // Silent - we already have the local cached user as fallback.
      }
    })();
  }, [setLocation]);

  // Fetch companies
  const { data: companies } = useQuery({
    queryKey: ["/api/companies/my"],
    queryFn: async () => {
      const res = await fetch(getApiUrl("/api/companies/my"), { headers: getAuthHeaders() });
      if (!res.ok) return [];
      const data = await res.json();
      return data.data || data || [];
    },
    enabled: !!user,
  });

  const getUserInitials = () => {
    if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
    if (firstName) return firstName.substring(0, 2).toUpperCase();
    if (user?.email) return user.email.substring(0, 2).toUpperCase();
    return "U";
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast({ title: t("common.error"), description: t("ui.only_jpg_png_gif_or_webp"), variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: t("common.error"), description: t("ui.max_5mb"), variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const imageUrl = reader.result as string;
      const updated = { ...user, profileImageUrl: imageUrl };
      setUser(updated);
      localStorage.setItem("user", JSON.stringify(updated));
      toast({ title: language === "pt" ? "Foto atualizada" : "Picture updated" });
    };
    reader.readAsDataURL(file);
  };

  const saveProfile = async () => {
    if (!firstName.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(getApiUrl("/api/auth/profile"), {
        method: "PATCH",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim(), avatarUrl: user?.profileImageUrl }),
      });
      if (res.ok) {
        const updated = { ...user, firstName: firstName.trim(), lastName: lastName.trim() };
        setUser(updated);
        localStorage.setItem("user", JSON.stringify(updated));
        setDirty(false);
        toast({ title: language === "pt" ? "Perfil salvo" : "Profile saved" });
      }
    } catch {
      toast({ title: t("common.error"), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast({ title: language === "pt" ? "Senha deve ter pelo menos 6 caracteres" : "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: language === "pt" ? "Senhas não conferem" : "Passwords don't match", variant: "destructive" });
      return;
    }
    // If user must change password (invited), currentPassword is not required
    const isMustChange = user?.mustChangePassword === true;
    if (!isMustChange && !currentPassword) {
      toast({ title: language === "pt" ? "Informe a senha atual" : "Current password is required", variant: "destructive" });
      return;
    }
    setChangingPassword(true);
    try {
      const body: any = { newPassword };
      if (currentPassword) body.currentPassword = currentPassword;
      const res = await fetch(getApiUrl("/api/auth/change-password"), {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => null);
      if (res.ok) {
        // Update token and user if returned
        if (data?.token) localStorage.setItem("token", data.token);
        if (data?.user) {
          const updatedUser = { ...user, ...data.user, mustChangePassword: false };
          setUser(updatedUser);
          localStorage.setItem("user", JSON.stringify(updatedUser));
        }
        setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
        toast({ title: language === "pt" ? "Senha alterada com sucesso!" : "Password changed successfully!" });
      } else {
        toast({ title: data?.message || data?.errors?.[0] || "Error", variant: "destructive" });
      }
    } catch {
      toast({ title: t("common.error"), variant: "destructive" });
    } finally {
      setChangingPassword(false);
    }
  };

  const planIcons: Record<string, any> = { free: Zap, pro: Star, enterprise: Crown, custmenterprise: Crown };
  const planColors: Record<string, string> = {
    free: "bg-gray-100 text-gray-700",
    pro: "bg-blue-100 text-blue-700",
    enterprise: "bg-green-100 text-green-700",
    custmenterprise: "bg-purple-100 text-purple-700",
  };

  if (!user) return null;

  const PlanIcon = planIcons[user.plan] || Zap;
  const planName = user.plan ? user.plan.charAt(0).toUpperCase() + user.plan.slice(1) : "Free";
  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    setLocation("/login");
  };

  // Inside the app shell like every other page, so the menu (and the way back) exists here too.
  return (
    <AppLayout user={user} onLogout={handleLogout} plan={user.plan} currentPage="/profile">
    <div className="space-y-6 max-w-2xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <User className="h-6 w-6" /> {t("profile")}
        </h1>
        <p className="text-muted-foreground mt-1">
          {language === "pt" ? "Gerencie suas informações pessoais" : "Manage your personal information"}
        </p>
      </div>

      {/* Avatar & Name Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Avatar */}
            <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
              <Avatar className="h-24 w-24">
                <AvatarImage src={user.profileImageUrl} alt={firstName} />
                <AvatarFallback className="text-2xl">{getUserInitials()}</AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="h-6 w-6 text-white" />
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
            </div>

            {/* Name fields */}
            <div className="flex-1 space-y-4 w-full">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>{language === "pt" ? "Nome" : "First Name"}</Label>
                  <Input
                    value={firstName}
                    onChange={(e) => { setFirstName(e.target.value); setDirty(true); }}
                    placeholder="John"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === "pt" ? "Sobrenome" : "Last Name"}</Label>
                  <Input
                    value={lastName}
                    onChange={(e) => { setLastName(e.target.value); setDirty(true); }}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5" /> {t("ui.email")}
                </Label>
                <Input value={user.email || ""} readOnly className="bg-muted" />
                <p className="text-xs text-muted-foreground">
                  {language === "pt" ? "O email não pode ser alterado" : "Email cannot be changed"}
                </p>
              </div>

              {user.hasCpf && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Shield className="h-3.5 w-3.5" /> CPF
                  </Label>
                  <Input
                    value={user.cpfMasked || "***.***.***-**"}
                    readOnly
                    disabled
                    className="bg-muted font-mono"
                    data-testid="input-cpf-readonly"
                  />
                  <p className="text-xs text-muted-foreground">
                    {language === "pt"
                      ? "O CPF não pode ser alterado. Entre em contato com o suporte se precisar corrigi-lo."
                      : "CPF cannot be changed. Contact support if you need to correct it."}
                  </p>
                </div>
              )}

              {dirty && (
                <Button onClick={saveProfile} disabled={saving || !firstName.trim()} className="w-full sm:w-auto">
                  <Save className="h-4 w-4 mr-2" />
                  {saving
                    ? (language === "pt" ? "Salvando..." : "Saving...")
                    : (language === "pt" ? "Salvar alterações" : "Save changes")}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plan Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4" />
            {language === "pt" ? "Plano Atual" : "Current Plan"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${planColors[user.plan] || planColors.free}`}>
                <PlanIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold">{planName}</p>
                <p className="text-sm text-muted-foreground">
                  {user.plan === "free"
                    ? (language === "pt" ? "3 documentos grátis" : "3 free documents")
                    : (language === "pt" ? "Documentos ilimitados" : "Unlimited documents")}
                </p>
              </div>
            </div>
            {user.plan === "free" && (
              <Button variant="outline" size="sm" onClick={() => setLocation("/settings")}>
                {language === "pt" ? "Fazer upgrade" : "Upgrade"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Companies */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            {language === "pt" ? "Empresas" : "Companies"}
          </CardTitle>
          <CardDescription>
            {language === "pt" ? "Empresas que você está vinculado" : "Companies you are associated with"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {companies && companies.length > 0 ? (
            <div className="space-y-3">
              {companies.map((c: any) => (
                <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                  <div className="flex items-center gap-3">
                    {c.logoUrl ? (
                      <img src={c.logoUrl.startsWith("http") ? c.logoUrl : getApiUrl(c.logoUrl)} alt={c.name} className="h-8 w-8 rounded-md object-cover" />
                    ) : (
                      <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-sm">{c.name}</p>
                      {c.cnpj && <p className="text-xs text-muted-foreground">CNPJ: {c.cnpj}</p>}
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {c.role === "owner" ? (language === "pt" ? "Proprietário" : "Owner")
                      : c.role === "admin" ? "Admin"
                      : c.role === "member" ? (language === "pt" ? "Membro" : "Member")
                      : (language === "pt" ? "Visualizador" : "Viewer")}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                {language === "pt" ? "Você não está vinculado a nenhuma empresa" : "You are not associated with any company"}
              </p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => setLocation("/settings?action=create-company")}>
                {language === "pt" ? "Criar empresa" : "Create company"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {language === "pt" ? "Informações da Conta" : "Account Information"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 text-sm">
            <div className="flex justify-between py-1.5 border-b">
              <span className="text-muted-foreground">ID</span>
              <span className="font-mono text-xs">{user.id?.substring(0, 8)}...</span>
            </div>
            <div className="flex justify-between py-1.5 border-b">
              <span className="text-muted-foreground">{language === "pt" ? "Função" : "Role"}</span>
              <Badge variant="outline">{user.role || "user"}</Badge>
            </div>
            {user.companyName && (
              <div className="flex justify-between py-1.5 border-b">
                <span className="text-muted-foreground">{language === "pt" ? "Empresa ativa" : "Active company"}</span>
                <span className="font-medium">{user.companyName}</span>
              </div>
            )}
            <div className="flex justify-between py-1.5">
              <span className="text-muted-foreground">{language === "pt" ? "Provedor de login" : "Login provider"}</span>
              <span>{user.provider || "email"}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password — only for local (email/password) users */}
      {(!user.provider || user.provider === "local") && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="h-4 w-4" />
              {language === "pt" ? "Alterar Senha" : "Change Password"}
            </CardTitle>
            {user.mustChangePassword && (
              <CardDescription className="text-amber-600 font-medium">
                {language === "pt"
                  ? "Você precisa definir uma nova senha para continuar usando o sistema."
                  : "You need to set a new password to continue using the system."}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {!user.mustChangePassword && (
              <div className="space-y-2">
                <Label>{language === "pt" ? "Senha atual" : "Current password"}</Label>
                <div className="relative">
                  <Input
                    type={showCurrentPw ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                  <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowCurrentPw(!showCurrentPw)}>
                    {showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>{language === "pt" ? "Nova senha" : "New password"}</Label>
              <div className="relative">
                <Input
                  type={showNewPw ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={language === "pt" ? "Mínimo 6 caracteres" : "Minimum 6 characters"}
                />
                <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowNewPw(!showNewPw)}>
                  {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{language === "pt" ? "Confirmar nova senha" : "Confirm new password"}</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-sm text-destructive">
                  {language === "pt" ? "Senhas não conferem" : "Passwords don't match"}
                </p>
              )}
            </div>
            <Button onClick={changePassword} disabled={changingPassword || !newPassword || newPassword !== confirmPassword}>
              {changingPassword ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  {language === "pt" ? "Salvando..." : "Saving..."}
                </span>
              ) : (
                <span>{language === "pt" ? "Alterar senha" : "Change password"}</span>
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
    </AppLayout>
  );
}
